import { useEffect, useState } from 'react'
import { listLiveLogs, fetchLiveLog, matchLiveLog } from '../lib/api'

interface LiveLogTarget {
  persona?: string
  worker?: string
  taskId?: string
  /** Label shown in the modal header (the clicked log line summary). */
  label: string
}

interface LiveLogModalProps {
  target: LiveLogTarget | null
  onClose: () => void
  getAuthHeaders: () => Record<string, string>
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'none' } // no matching live log exists
  | { kind: 'loaded'; content: string; gitRef: string; path: string }

/**
 * Drill-down viewer for a full agent response. The activity feed only carries
 * one-liners; the full per-loop response lives in
 * `.starforge/logs/live/<persona>-<worker>-<task>.log` while in-flight and is
 * preserved to `.starforge/logs/transcripts/…` once the task completes (#4),
 * both pushed to git by cloud agents. We fetch it ON DEMAND (only when this
 * modal opens), never on poll: first the cheap cross-branch listing, then the
 * single matched file's content.
 */
export function LiveLogModal({ target, onClose, getAuthHeaders }: LiveLogModalProps) {
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    if (!target) return
    let cancelled = false
    setState({ kind: 'loading' })

    ;(async () => {
      try {
        const refs = await listLiveLogs(getAuthHeaders)
        const match = matchLiveLog(refs, target)
        if (!match) {
          if (!cancelled) setState({ kind: 'none' })
          return
        }
        const content = await fetchLiveLog(match.path, match.git_ref, getAuthHeaders)
        if (!cancelled) setState({ kind: 'loaded', content, gitRef: match.git_ref, path: match.path })
      } catch (e: unknown) {
        if (!cancelled) setState({ kind: 'error', message: e instanceof Error ? e.message : 'fetch failed' })
      }
    })()

    return () => { cancelled = true }
  }, [target, getAuthHeaders])

  // ESC to close
  useEffect(() => {
    if (!target) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [target, onClose])

  if (!target) return null

  return (
    <div
      data-testid="live-log-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex flex-col max-h-[80vh] w-full max-w-3xl bg-gray-900 border border-cosmic-border rounded-lg overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-2 flex-shrink-0">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-200 truncate">Full agent response</div>
            <div className="text-xs text-gray-500 truncate">{target.label}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close live log"
            className="text-gray-500 hover:text-gray-300 text-sm px-2"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-black min-h-0">
          {state.kind === 'loading' && (
            <div className="text-sm text-gray-500">Loading full response…</div>
          )}
          {state.kind === 'error' && (
            <div className="text-sm text-red-400">Could not load live log: {state.message}</div>
          )}
          {state.kind === 'none' && (
            <div className="text-sm text-gray-500">
              No full-response log found for this entry. Transcripts are preserved once a
              task completes, but very old tasks (before this was enabled) or logs dropped
              in a sync conflict may only show the activity one-liner.
            </div>
          )}
          {state.kind === 'loaded' && (
            <>
              <div className="text-xs text-gray-600 mb-2">
                {state.path} <span className="text-gray-700">@ {state.gitRef}</span>
              </div>
              <pre className="font-mono text-xs leading-relaxed text-gray-300 whitespace-pre-wrap break-words">
                {state.content}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
